import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from 'react-router-dom';

import styles from './Login.module.scss';
import { Text } from '../../components/shared/Typography/Text/Text';
import { userActions } from '../../redux/modules/auth/actions';
import { LoginUserType } from '../../redux/modules/auth/types';
import { PageLayoutAuth } from '../../components/shared/PageLayout/PageLayoutAuth/PageLayoutAuth';
import { Row, RowCell } from '../../components/shared/Row/Row';
import { Input } from '../../components/shared/Input/Input';
import { Button } from '../../components/shared/Button/Button';
import { Separator } from '../../components/shared/Separator/Separator';
import { CountryLanguageSwitcher } from '../../components/shared/CountryLanguageSwitcher/CountryLanguageSwitcher';
import { useForm } from 'react-hook-form';
import { TranslateMessage } from '../../components/shared/Translations/TranslateMessage/TranslateMessage';
import { useIntl } from 'react-intl';
import {
  getAuthSelector,
  getAuthErrorSelector,
} from '../../redux/modules/auth/selectors';
import { createSelector } from 'reselect';
import {
  countrySelector,
  languageSelector,
} from '../../redux/modules/intl/selectors';
import intlSlice from '../../redux/modules/intl/intlSlice';
import { splitLocale } from '../../redux/modules/intl/utils';
import { CountryLanguageItem } from '../../components/shared/CountryLanguageSwitcher/CountryLanguageItem/CountryLanguageItem';
import { supportedCountries } from '../../components/shared/CountryLanguageSwitcher/utils';
import { Column } from '../../components/shared/Column/Column';
import { LoginSchema } from '../../utils/authValidationSchema';
import { ToastType } from '../../components/shared/Toast/types';
import { useDispatchToast } from '../../hooks/useDispatchToast/useDispatchToast';
import authSlice from '../../redux/modules/auth/authSlice';
import { UsaTermsAndConditionsModal } from '../../components/shared/Modal/USTermsAndConditionsModal/UsaTermsAndConditionsModal';
import { EmailInputField } from '../../components/shared/AuthFields/EmailInputField';
import { useGetPageUrl } from '../../AppRouter/utils';
import { CountryUtils } from '../../utils/CountryUtils';
import { useUrlQuery } from '../../hooks/useUrlQuery/useUrlQuery';

const mapStateToProps = createSelector(
  getAuthSelector,
  countrySelector,
  languageSelector,
  getAuthErrorSelector,
  (auth, country, language, authError) => ({
    status: auth.status,
    country,
    language,
    authError,
  }),
);

export const LoginPage: React.FC = () => {
  const intl = useIntl();
  const urlSearchParams = useUrlQuery();
  const history = useHistory();
  const { registrationPageUrl, forgotPasswordPageUrl } = useGetPageUrl();
  const [
    isUsaTermsAndConditionModalVisible,
    setIsUsaTermsAndConditionModalVisible,
  ] = useState(false);
  const dispatchToast = useDispatchToast();
  const dispatchRedux = useDispatch();
  const { status, country, language, authError } = useSelector(mapStateToProps);
  const { register, handleSubmit, watch, errors } = useForm({
    resolver: yupResolver(LoginSchema),
    mode: 'onTouched',
  });
  const email = watch('email');

  const isJapan = country === CountryUtils.COUNTRY.JAPAN;

  useEffect(() => {
    if (authError.message) {
      dispatchToast({
        type: ToastType.DANGER,
        message: {
          title: intl.formatMessage({
            id: authError.message,
          }),
        },
      });
      dispatchRedux(authSlice.actions.resetAuthState());
    }
  }, [authError, dispatchToast, intl, dispatchRedux]);

  const login = (userData: LoginUserType) => {
    const urlSearchParamsString = urlSearchParams.toString();

    dispatchRedux(
      userActions.login(
        userData,
        true,
        urlSearchParamsString
          ? `/${country}/${language}_${country}/login?${urlSearchParamsString}`
          : null,
      ),
    );
  };

  const handleLanguageChange = (propName: string, value: string) => {
    if (propName === 'locale') {
      const { language, country } = splitLocale(value);
      dispatchRedux(intlSlice.actions.changeLocale(`${language}_${country}`));
      history.push(
        `/${country}/${language}_${country}/login?${urlSearchParams}`,
      );
    }
  };

  const isSubmitButtonEnabled =
    !!email && !!watch('password') && !errors.email && !errors.password;

  const locale = `${language}_${country}`;
  const supportedCountry = supportedCountries.find(
    item => item.locale === locale,
  );
  const handleGoToRegistrationPage = () => {
    history.push(`${registrationPageUrl}?email=${email}`);
  };
  const handleGoToForgotPasswordPage = () => {
    history.push(`${forgotPasswordPageUrl}?email=${email}`);
  };

  const handleUsaTermsAndCondtionsModal = () =>
    setIsUsaTermsAndConditionModalVisible(!isUsaTermsAndConditionModalVisible);

  const isUsa = country === 'us';
  const privacyLink = isJapan
    ? CountryUtils.getJapanPrivacyLink()
    : `/docs/privacy/${locale}.html`;

  return (
    <PageLayoutAuth hasLogo country={country}>
      <Column minHeight={420}>
        <form name="form" onSubmit={handleSubmit(login)}>
          <Row>
            <RowCell>
              <TranslateMessage tag="h5" id="component.LoginPage.title" />
            </RowCell>
          </Row>

          <EmailInputField register={register} errors={errors} />

          <Row>
            <RowCell>
              <Input
                name="password"
                register={register}
                isPassword
                width={300}
                placeholder={intl.formatMessage({
                  id: 'component.RegistrationUserPassword.passwordPlaceholder',
                })}
                seleniumAttribute={'login-password-input'}
                theme={'grey'}
                tooltipErrorMessage={
                  errors.password ? (
                    <TranslateMessage id="component.RegisterPage.passwordRequired" />
                  ) : (
                    undefined
                  )
                }
                tooltipPlacement="right"
              />
            </RowCell>
          </Row>
          <Row margin={'small'}>
            <RowCell>
              <Button
                typeOperation={'submit'}
                type={'primary'}
                seleniumAttribute={'login-submit-button'}
                label={<TranslateMessage id="component.LoginPage.signIn" />}
                width={300}
                isDisabled={!isSubmitButtonEnabled}
                isLoading={status === 'loading'}
              />
            </RowCell>
          </Row>
          <Row justify={'center'}>
            <RowCell auto>
              <Button
                type={'secondary'}
                label={
                  <TranslateMessage id="component.LoginPage.forgotPassword" />
                }
                size={'small'}
                onClick={handleGoToForgotPasswordPage}
              />
            </RowCell>
          </Row>

          <Separator margin={'large'} transparent />

          <Row>
            <RowCell>
              <Button
                type={'primary'}
                label={
                  <TranslateMessage id="component.LoginPage.signUpForFree" />
                }
                width={300}
                onClick={handleGoToRegistrationPage}
              />
            </RowCell>
          </Row>

          <Separator margin={'small'} transparent />

          <Row justify={'center'}>
            <RowCell auto>
              <CountryLanguageSwitcher
                withPhoneCode={false}
                country={country}
                language={language}
                handleChange={handleLanguageChange}
                button={
                  supportedCountry ? (
                    <div>
                      <CountryLanguageItem
                        showLanguage={
                          country === CountryUtils.COUNTRY.BELGIUM ||
                          country === CountryUtils.COUNTRY.INDIA
                        }
                        size={'medium'}
                        countryTranslationKey={
                          supportedCountry.countryTranslationKey
                        }
                        languageTranslationKey={
                          supportedCountry.languageTranslationKey
                        }
                        countryCode={country}
                        countryClassName={styles.country}
                      />
                    </div>
                  ) : (
                    <Button
                      type={'secondary'}
                      label={<TranslateMessage id="general.change" />}
                      size={'small'}
                    />
                  )
                }
              />
            </RowCell>
          </Row>

          {isUsa && (
            <Row margin={'smaller'}>
              <RowCell align={'center'}>
                <TranslateMessage id="component.LoginPage.weCollectData" />
                <Button
                  onClick={handleUsaTermsAndCondtionsModal}
                  type="secondary"
                  size="medium"
                  label={<TranslateMessage id="component.LoginPage.ccpa" />}
                />
              </RowCell>
            </Row>
          )}

          <Row margin={'smaller'}>
            <RowCell align={'center'}>
              <TranslateMessage
                tag="btn2"
                id="component.LoginPage.companyInfo"
                className={styles.footerCopyright}
              />
            </RowCell>
          </Row>

          <Row>
            <RowCell align={'center'}>
              <Text type={'btn2'} className={styles.footerLinks}>
                <a
                  href={`/docs/tc/${locale}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TranslateMessage id="general.termsAndConditions" />
                </a>
                {' | '}
                <a href={privacyLink} target="_blank" rel="noopener noreferrer">
                  <TranslateMessage id="component.UserSettingsPage.privacyPolicy" />
                </a>
                {' | '}
                <a
                  href={`/docs/support/${locale}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TranslateMessage id="component.UserSettingsPage.support" />
                </a>
                {' | '}
                <a
                  href={`/docs/faq/${locale}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TranslateMessage id="general.faq" />
                </a>
                {' | '}
                <a
                  href={`/docs/imprint/${locale}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TranslateMessage id="component.UserSettingsPage.imprint" />
                </a>
              </Text>
            </RowCell>
          </Row>
        </form>
      </Column>
      {isUsaTermsAndConditionModalVisible && (
        <UsaTermsAndConditionsModal onClose={handleUsaTermsAndCondtionsModal} />
      )}
    </PageLayoutAuth>
  );
};

export default LoginPage;
